<template>
    <div class="flex flex-col gap-2">
        <!-- Selected connection -->
        <div v-if="selectedConnection" class="flex items-start justify-between gap-2">
            <div class="flex flex-col gap-0.5">
                <p class="nadia-title-subsection text-valence-grey-850">{{ selectedConnection.name }}</p>
                <p v-if="selectedConnection.job_title" class="body-small text-valence-grey-750">{{ selectedConnection.job_title }}</p>
            </div>
            <button type="button" class="shrink-0 details text-valence-grey-500 hover:text-valence-grey-800 mt-1" @click="handleClear">Change</button>
        </div>

        <!-- Search -->
        <template v-else>
            <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                aria-label="Search connections"
                placeholder="Search connections…"
                class="w-full rounded-lg border border-valence-grey-250 px-3 py-2 body-small text-valence-grey-850 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-500"
                @keydown="handleKeydown" />
            <div v-if="isFetching" class="flex justify-center py-2">
                <i class="bi bi-arrow-clockwise animate-spin text-valence-grey-500" aria-hidden="true"></i>
            </div>
            <ul v-else-if="results.length > 0" class="flex flex-col gap-0.5 max-h-60 overflow-y-auto">
                <li v-for="(conn, index) in results" :key="conn.id">
                    <button
                        type="button"
                        class="w-full text-left cursor-pointer rounded-lg px-2 py-1.5"
                        :class="{ 'bg-valence-primary-500 text-white': index === highlightedIndex }"
                        @click="handleSelect({ id: conn.id, name: conn.persona.name, job_title: conn.connection.job_title })"
                        @mouseenter="highlightedIndex = index"
                        @mouseleave="highlightedIndex = -1"
                        @focus="highlightedIndex = index"
                        @blur="highlightedIndex = -1">
                        <p class="body-small font-medium">{{ conn.persona.name }}</p>
                        <p v-if="conn.connection.job_title" class="details" :class="index === highlightedIndex ? 'text-white/80' : 'text-valence-grey-500'">
                            {{ conn.connection.job_title }}
                        </p>
                    </button>
                </li>
            </ul>
            <p v-else-if="showNoResults" class="body-small text-valence-grey-500 px-1">No results found.</p>
            <p class="details text-valence-grey-500 px-1 pt-2 border-t border-valence-grey-250 mt-1">
                You can add new connections from the <a href="/people" target="_blank" class="text-valence-primary-500 hover:underline">My People</a> page.
            </p>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { computed, nextTick, ref, watch } from "vue";

interface ConnectionResult {
    id: string;
    connection: { job_title: string | null };
    persona: { name: string };
}

interface ConnectionListResponse {
    connections: ConnectionResult[];
    has_more: boolean;
}

export interface SelectedConnection {
    id: string;
    name: string | null;
    job_title: string | null;
}

const props = defineProps<{
    autoFocus?: boolean;
}>();

const emit = defineEmits<{
    select: [connection: SelectedConnection];
    clear: [];
}>();

const searchInputRef = ref<HTMLInputElement | null>(null);

watch(searchInputRef, (el) => {
    if (el && props.autoFocus) {
        nextTick(() => el.focus());
    }
});

const searchQuery = ref("");
const results = ref<ConnectionResult[]>([]);
const isDebouncing = ref(false);
const selectedConnection = ref<SelectedConnection | null>(null);
const highlightedIndex = ref(-1);

watch(results, () => {
    highlightedIndex.value = -1;
});

function handleKeydown(e: KeyboardEvent) {
    if (results.value.length === 0) {
        return;
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value + 1) % results.value.length;
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value - 1 + results.value.length) % results.value.length;
    } else if (e.key === "Enter" && highlightedIndex.value >= 0) {
        e.preventDefault();
        const conn = results.value[highlightedIndex.value];
        handleSelect({ id: conn.id, name: conn.persona.name, job_title: conn.connection.job_title });
    }
}

const searchUrl = computed(() => `/v1/connections/?search=${encodeURIComponent(searchQuery.value)}&page_size=10`);
const showNoResults = computed(() => searchQuery.value.trim().length > 0 && !isFetching.value && !isDebouncing.value);
const { data: searchData, execute: runSearch, isFetching } = useFetch(searchUrl, { immediate: false }).json<ConnectionListResponse>();

watch(searchData, (val) => {
    results.value = val?.connections ?? [];
});

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(searchQuery, (val) => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
    if (!val.trim()) {
        results.value = [];
        isDebouncing.value = false;
        return;
    }
    isDebouncing.value = true;
    debounceTimer = setTimeout(() => {
        isDebouncing.value = false;
        runSearch();
    }, 300);
});

function handleSelect(connection: SelectedConnection) {
    selectedConnection.value = connection;
    searchQuery.value = "";
    results.value = [];
    isDebouncing.value = false;
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
    emit("select", connection);
}

function handleClear() {
    selectedConnection.value = null;
    emit("clear");
}

function reset() {
    selectedConnection.value = null;
    searchQuery.value = "";
    results.value = [];
    isDebouncing.value = false;
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
}

defineExpose({ reset, focus: () => searchInputRef.value?.focus() });
</script>
